// Treat these backend "sentinel" dates as empty/unassigned
const UNASSIGNED_DATE_REGEX =
    /^0001-01-01(?:T00:00:00(?:\.0+)?(?:Z|[+-]\d{2}:\d{2})?)?$/i;

const ZERO_DATE_REGEX = /^0{4}-0{2}-0{2}/i;

function isUnassignedDate(value) {
    if (value == null) return true;

    // Strings coming from API
    if (typeof value === "string") {
        const s = value.trim();
        if (!s) return true;
        if (UNASSIGNED_DATE_REGEX.test(s)) return true;
        if (ZERO_DATE_REGEX.test(s)) return true;
        return false;
    }

    // Date instances
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return true;

        // Match 0001-01-01 00:00:00 (UTC) to avoid local timezone shifts
        return (
            value.getUTCFullYear() === 1 &&
            value.getUTCMonth() === 0 &&
            value.getUTCDate() === 1 &&
            value.getUTCHours() === 0 &&
            value.getUTCMinutes() === 0 &&
            value.getUTCSeconds() === 0 &&
            value.getUTCMilliseconds() === 0
        );
    }

    return false;
}

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_TIMESTAMP_REGEX =
    /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

export function parseUtcTimestamp(value) {
    if (isUnassignedDate(value)) return null;

    let normalizedValue = value;

    if (typeof value === "string") {
        normalizedValue = value.trim();
        const dateOnlyMatch = normalizedValue.match(DATE_ONLY_REGEX);

        // Some historical timestamp fields contain only a calendar date. Keep
        // that date stable because there is no time-of-day to convert.
        if (dateOnlyMatch) {
            const [, year, month, day] = dateOnlyMatch;
            normalizedValue = new Date(
                Number(year),
                Number(month) - 1,
                Number(day),
            );
        } else if (ISO_TIMESTAMP_REGEX.test(normalizedValue)) {
            // The API stores timestamp fields in UTC, but older responses can
            // omit the offset. An ISO value with an explicit offset is left as-is.
            normalizedValue = `${normalizedValue.replace(" ", "T")}Z`;
        }
    }

    const d =
        normalizedValue instanceof Date
            ? normalizedValue
            : new Date(normalizedValue);
    if (Number.isNaN(d.getTime())) return null;

    // If parsing resulted in the sentinel date, treat it as null too
    if (isUnassignedDate(d)) return null;

    return d;
}

// Optional micro-optimization: cache formatters (tables love this)
const getFormatter = (() => {
    const cache = new Map();
    return (options) => {
        const locale = "en-US";
        const key = `${locale}:${JSON.stringify(options)}`;
        if (!cache.has(key))
            cache.set(key, new Intl.DateTimeFormat(locale, options));
        return cache.get(key);
    };
})();

export const formatDateTime = (value) => {
    const d = parseUtcTimestamp(value);
    if (!d) return "Not assigned";

    const date = getFormatter({
        year: "numeric",
        month: "numeric",
        day: "numeric",
    }).format(d);
    const time = getFormatter({
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    }).format(d);

    return `${date} ${time}`;
};

export const formatTimestampDate = (value, options = { dateStyle: "short" }) => {
    const d = parseUtcTimestamp(value);
    if (!d) return "";

    return getFormatter(options).format(d);
};

export const formatCompleteDateTime = (date, status) => {
    const isComplete = status === "Complete" || status === 2;

    const d = parseUtcTimestamp(date);
    if (!d) return isComplete ? "Not assigned" : "Not completed";

    return getFormatter({ dateStyle: "short", timeStyle: "short" }).format(d);
};
